import { createFileRoute, type LinkProps } from "@tanstack/react-router";
import { ColumnDef } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";

import { Header } from "@/components/universal/header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTable,
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  useGetRecall,
  useGetRecallPatients,
} from "@/requests/gen/react-query/recall";
import { RecallPatientRead } from "@/requests/gen/react-query/fastAPI.schemas";
import { safeFormat } from "@/lib/safe-date-format";
import { SeverityBadge } from "../-severity-badge";
import parsePhoneNumber from "libphonenumber-js";
import { useQueryStates } from "nuqs";

export const Route = createFileRoute(
  "/_private/_inventory-360/recall/$recallEventId/",
)({
  component: RecallDetail,
});

type PatientRow = RecallPatientRead & { link?: LinkProps };

const patientColumns: ColumnDef<PatientRow>[] = [
  {
    header: "Patient",
    accessorKey: "last_name",
    cell: ({ row }) => `${row.original.first_name} ${row.original.last_name}`,
  },
  {
    header: "Date of Birth",
    accessorKey: "date_of_birth",
    cell: ({ row }) => safeFormat(row.original.date_of_birth),
  },
  {
    header: "Phone",
    accessorKey: "phone",
    cell: ({ row }) => {
      const phone = row.original.phone ?? "";
      return parsePhoneNumber(phone)?.formatNational() ?? "--";
    },
  },
  {
    header: "Email",
    accessorKey: "email",
    cell: ({ row }) => row.original.email ?? "--",
  },
  {
    header: "Rx #",
    accessorKey: "rx_number",
    cell: ({ row }) => row.original.rx_number ?? "--",
  },
  {
    header: "Qty",
    accessorKey: "quantity",
  },
  {
    header: "Dispensed",
    accessorKey: "dispensed_at",
    cell: ({ row }) => safeFormat(row.original.dispensed_at),
  },
  {
    header: "Prescriber",
    accessorKey: "doctor_name",
  },
];

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function RecallDetail() {
  const { recallEventId } = Route.useParams();
  const id = Number(recallEventId);
  const [dataTableParams] = useQueryStates(dataTableSearchParsers);

  const recall = useGetRecall(id);
  const patients = useGetRecallPatients(
    id,
    {
      page: dataTableParams.page,
      limit: dataTableParams.limit,
      search: dataTableParams.search,
    },
    { query: { placeholderData: keepPreviousData } },
  );

  return (
    <div>
      <title>{`Lot ${recall.data?.lot_number ?? "Unassigned"} Recall - Legrande Health Portal`}</title>
      <Header
        crumbs={[
          { name: "Recalls", link: { to: "/recall/lookup" } },
          {
            name: recall.data?.product_name ?? "Recall",
            link: {
              to: "/recall/$recallEventId",
              params: { recallEventId },
            },
          },
        ]}
      />

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Recall Details</CardTitle>
          <CardDescription>
            {recall.data
              ? `${recall.data.product_name} — Lot ${recall.data.lot_number ?? "Unassigned"}`
              : "Loading recall..."}
          </CardDescription>
          {recall.data?.signed_url && (
            <CardAction>
              <Button variant="outline" asChild>
                <a
                  href={recall.data.signed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink />
                  View Letter
                </a>
              </Button>
            </CardAction>
          )}
        </CardHeader>
        <CardContent>
          {recall.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="animate-spin size-4" />
              Loading...
            </div>
          ) : recall.isError || !recall.data ? (
            <p className="text-destructive">Recall not found.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              <DetailRow
                label="Severity"
                value={<SeverityBadge severity={recall.data.severity} />}
              />
              <DetailRow label="Product" value={recall.data.product_name} />
              <DetailRow label="Practice" value={recall.data.entity_name} />
              <DetailRow
                label="Lot Number"
                value={recall.data.lot_number ?? "Unassigned"}
              />
              <DetailRow
                label="Recall Date"
                value={safeFormat(recall.data.recall_date)}
              />
              <DetailRow
                label="Created"
                value={safeFormat(recall.data.created_at)}
              />
              <DetailRow label="Reason" value={recall.data.reason ?? "--"} />
              <DetailRow label="Notes" value={recall.data.notes ?? "--"} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Affected Patients</CardTitle>
          <CardDescription>
            Patients who received product dispensed from this lot.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={patients.isFetching} />
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={patientColumns}
            data={(patients.data?.items ?? []) as PatientRow[]}
            isLoading={patients.isLoading}
            isError={patients.isError}
            count={patients.data?.count}
          />
        </CardContent>
      </Card>
    </div>
  );
}
